import { DefaultQueryParams } from '@frontend/api-utils';
import { ChartClient } from '@frontend/chart/api';
import { TransactionTimerPickUpPendingCompletedVolumeBarChart } from '@frontend/chart/components';
import { Chart, ChartBinSize, ChartQueryParam } from '@frontend/chart/types';
import { Application } from '@frontend/functionality/types';
import { SlotClient } from '@frontend/slot/api';
import { useEffect, useState } from 'react';

import { getLast30DaysChartRange } from '../../../statistics/pudo/last-30-days-chart-range';
import { useInsightFetchRange } from '../insight-detail/use-insight-fetch-range';

const toDailyTurnoverRateChart = (completedPickUps: Chart, slotCount: number): Chart | undefined => {
    if (slotCount <= 0) {
        return undefined;
    }

    const volumeSeries = completedPickUps.series[0];
    if (!volumeSeries?.data?.length) {
        return undefined;
    }

    return {
        series: [
            {
                name: volumeSeries.name,
                data: volumeSeries.data.map((point) => ({
                    label: point.label,
                    value: Math.round((point.value / slotCount) * 100) / 100
                }))
            }
        ]
    };
};

export const useNivalizDailyTurnoverRateLineChart = (accountId: string) => {
    const [data, setData] = useState<Chart | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(true);

    const fetchRange = useInsightFetchRange(getLast30DaysChartRange, { defaultBinSize: ChartBinSize.DAY, useDetailBinSize: true });

    useEffect(() => {
        if (!accountId) {
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);

        const { after, before } = fetchRange;

        Promise.all([
            ChartClient.fetchChartData(accountId, TransactionTimerPickUpPendingCompletedVolumeBarChart.definition, {
                [DefaultQueryParams.AFTER]: after,
                [DefaultQueryParams.BEFORE]: before,
                ...fetchRange.entityParams,
                [ChartQueryParam.BIN_SIZE]: fetchRange.binSize!,
                [ChartQueryParam.APPLICATION]: Application.NIVALIZ
            }),
            SlotClient.fetchSlots({
                account_id: accountId,
                size: '1'
            })
        ])
            .then(([completedPickUps, slots]) => {
                if (cancelled) return;
                setData(toDailyTurnoverRateChart(completedPickUps, slots.count));
            })
            .catch(() => {
                if (!cancelled) {
                    setData(undefined);
                }
            })
            .finally(() => {
                if (!cancelled) {
                    setIsLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [accountId, fetchRange.key]);

    return {
        data,
        isLoading
    };
};
